import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { DepositPanel } from '../UserBlock';
import { BonanzaPromoSection } from './BonanzaPromoSection';
import { LoyaltySection, LoyaltySectionProps } from './LoyaltySection';
import { TopSpotGameSection } from './TopSpotGameSection';
import { DeskDashboardPanel } from '@/components/DeskDashboard';
import { LabelsSection } from '@/components/LabelsSection';
import { BonanzaPromoStatus } from '@/helpers/bonanzaPromo';
import { getImageSrc } from '@/helpers/common';
import { checkIsEasterPromoAvailable } from '@/helpers/easter';
import { TopSpotGameStatus } from '@/helpers/topSpotGame';
import { isWheelPromoAvailableDate } from '@/helpers/wheel';
import useEstonia from '@/hooks/estonia';
import { checkIsScratchGameAvailable } from '@/modules/scratchGame/helpers';
import { useCommonStore } from '@/store';

import { ScDashboardPanelPopup } from './styled';

export const LoyaltyDashboardDesktopPanel = () => {
  const loyaltyData = useCommonStore.use.getLoyaltyData()();

  return loyaltyData ? (
    <DeskDashboardPanel
      labelText={`${loyaltyData.level}`}
      icon={{
        width: 58,
        height: 32,
        src: '/loyalty/logo.png',
      }}
      popupContent={
        <ScDashboardPanelPopup>
          <LoyaltySection loyaltyData={loyaltyData} />
        </ScDashboardPanelPopup>
      }
    />
  ) : null;
};

export const BonanzaPromoDashboardDesktopPanel = () => {
  const bonanzaStaticData = useCommonStore.use.bonanzaStaticData();
  const userBonanzaGroup = useCommonStore.use.userBonanzaGroup();

  const isLockedPromo = useMemo(
    () => userBonanzaGroup === BonanzaPromoStatus.PROMO_LOCKED,
    [userBonanzaGroup]
  );

  return (
    <DeskDashboardPanel
      labelText={isLockedPromo ? '' : `${bonanzaStaticData.progressPercent}%`}
      icon={{
        width: 32,
        height: 32,
        src: getImageSrc(bonanzaStaticData.label),
      }}
      popupContent={
        <ScDashboardPanelPopup>
          <BonanzaPromoSection />
        </ScDashboardPanelPopup>
      }
    />
  );
};

export const TopSpotGameDashboardDesktopPanel = () => {
  const { t } = useTranslation('vip');

  const {
    userTSGProgressPercentByCP,
    topSpotGameStaticData,
    topSpotGameStatus,
    isPassedSuperSpotBreakpoint,
  } = useCommonStore(
    useShallow((state) => ({
      userTSGProgressPercentByCP: state.userTSGProgressPercentByCP,
      topSpotGameStaticData: state.topSpotGameStaticData,
      topSpotGameStatus: state.topSpotGameStatus,
      isPassedSuperSpotBreakpoint: state.isPassedSuperSpotBreakpoint,
    }))
  );

  if (!topSpotGameStaticData) return null;

  const labelText = useMemo(() => {
    if (topSpotGameStatus === TopSpotGameStatus.PVIP) return '';
    if (isPassedSuperSpotBreakpoint) return t('vip:dashboardPanel.next');

    return `${userTSGProgressPercentByCP}%`;
  }, [
    topSpotGameStatus,
    userTSGProgressPercentByCP,
    isPassedSuperSpotBreakpoint,
    t,
  ]);

  return (
    <DeskDashboardPanel
      labelText={labelText}
      icon={{
        width: 50,
        height: 30,
        src: topSpotGameStaticData?.dashboardImg,
      }}
      popupContent={
        <ScDashboardPanelPopup>
          <TopSpotGameSection />
        </ScDashboardPanelPopup>
      }
    />
  );
};

export const DashboardPanelMobile: FC<
  Pick<LoyaltySectionProps, 'onLinkClick'>
> = ({ onLinkClick }) => {
  const { isEstonia } = useEstonia();
  const loyaltyData = useCommonStore.use.getLoyaltyData()();
  const {
    user,
    scratchGameData,
    topSpotGameStatus,
    isTopSpotGameAvailable,
    isBonanzaPromoAvailable,
  } = useCommonStore(
    useShallow((state) => ({
      user: state.user,
      scratchGameData: state.scratchGameData,
      topSpotGameStatus: state.topSpotGameStatus,
      isTopSpotGameAvailable: state.isTopSpotGameAvailable,
      isBonanzaPromoAvailable: state.isBonanzaPromoAvailable,
    }))
  );

  const isAnyPromoAvailable = useMemo(
    () =>
      isWheelPromoAvailableDate ||
      isBonanzaPromoAvailable ||
      checkIsScratchGameAvailable(scratchGameData, user) ||
      checkIsEasterPromoAvailable(user),
    [isBonanzaPromoAvailable, scratchGameData, user]
  );

  return (
    <ScDashboardPanelPopup isMobile>
      <div className="content user-block">
        <DepositPanel mobile boldValue />
      </div>

      {topSpotGameStatus && isTopSpotGameAvailable && (
        <>
          <div className="divider" />
          <TopSpotGameSection isMobile onLinkClick={onLinkClick} />
        </>
      )}

      {loyaltyData && !isEstonia && !isAnyPromoAvailable && (
        <>
          <div className="divider" />
          <LoyaltySection
            loyaltyData={loyaltyData}
            isMobile
            onLinkClick={onLinkClick}
          />
        </>
      )}

      {isBonanzaPromoAvailable && (
        <>
          <div className="divider" />

          <BonanzaPromoSection isMobile onLinkClick={onLinkClick} />
        </>
      )}

      <LabelsSection onLabelClick={onLinkClick} />
    </ScDashboardPanelPopup>
  );
};
